import React, { useState, useEffect } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import {
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  Badge,
  Typography
} from '@material-ui/core';
import { DialogWithFocusProvider } from 'components/layout';
import ClearIcon from '@material-ui/icons/Clear';
import debounce from 'lodash/debounce';
import { Resizable } from 're-resizable';
import { LoadingOverlayWrapper} from 'components/layout';
import { PropertyUWContent, PropertyUWProforma } from 'components/underwrite/acquisition/propertyUnderwrite';
import LegacyPostAcqUWProforma from 'components/underwrite/acquisition/propertyUnderwrite/PostAcqUWProforma';
import { PostAcqUWContent, PostAcqUWProforma } from 'components/underwrite/postAcquisition/postAcqUnderwrite';
import UnderwriteHistoryPopover from 'components/explore/gmap/UnderwriteHistoryPopover';
import {
  setProformaWidth, fetchUWData, appNotify, setPropertyUWClientTxnOpp, setExploreViewUnderwriteModalIsOpen, fetchExplorePropertyUnderwrites,
  setSelectedPropertyUnderwrite
} from 'store/actions';
import { useSaveUWDialog } from 'components/underwrite/shared/SaveUWDialog';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { parseISO, format } from 'date-fns';

const ViewUnderwriteModal = ({ classes, open, handleClose, txnOppId, asgPropID, txnOppProformaIsOpen, proformaWidth, setProformaWidth,
  isFetchingPropertyUWData, isSyncingPurchaseState, propertyUWLoadingOverlayState,
  selectedOpportunity, fetchUWData, selectedContainer, channelWorkflowGridDataUpdate, appNotify,
  setPropertyUWClientTxnOpp, isPostAcquisitionUnderwrite, valuationIsDirty,
  setExploreViewUnderwriteModalIsOpen, fetchExplorePropertyUnderwrites, propertyUnderwritesData, selectedPropertyUnderwrite,
  isFetchingPostAcqUWData, postAcqProformaIsOpen, setSelectedPropertyUnderwrite
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
    
  const { openSaveUWDialog } = useSaveUWDialog();
  useEffect(() => {
    if (!open) return;
    let abortController = new AbortController();

    fetchExplorePropertyUnderwrites(asgPropID);

    return () => {
      abortController.abort();
    }
  }, [open]);

  useEffect(() => {
    if(channelWorkflowGridDataUpdate?.data) {
      const isOppDeleted = Object.entries(channelWorkflowGridDataUpdate.data).some(pair => parseInt(pair[0]) === selectedOpportunity?.id && pair[1].type === 'remove');
      if(isOppDeleted) {
        setPropertyUWClientTxnOpp(null);
        appNotify(`Opportunity ${selectedOpportunity?.id} has been deleted`, 'error')
        setAnchorEl(null);
        handleClose();
      }
    }
  }, [channelWorkflowGridDataUpdate]);

  const onResize = debounce(() => {
    setProformaWidth(proformaResizable.resizable.clientWidth);
  }, 100);

  const onClose = () => {
    if (valuationIsDirty) {
      openSaveUWDialog({ callback: () => {
        setExploreViewUnderwriteModalIsOpen(false);
      }});
    } else {
      handleClose();
    }
  }

  const handleChangeUnderwrite = e => {
    if (valuationIsDirty) {
      openSaveUWDialog({
        callback: () => {
          setSelectedPropertyUnderwrite(e.data);
          togglePopover();
        },
        saveUWDialogParameters: {} // Pass this to reset transient/clear dirty flag
      });
    } else {
      setSelectedPropertyUnderwrite(e.data);
      togglePopover();
    }
  }

  const togglePopover = (e) => {
    const el = anchorEl ? null : e.currentTarget;
    setAnchorEl(el);
  }

  let proformaResizable;  // Ref to resizable component, used to manually update size
  return (
    <DialogWithFocusProvider onClose={onClose} open={open} classes={{ root: classes.dialog, paper: classes.dialogPaper }} maxWidth={false} fullWidth={true}>
      <DialogTitle>
        {selectedPropertyUnderwrite 
          ? <Tooltip title='Click for Underwrite History'>
              <span onClick={togglePopover} style={{ cursor: 'pointer' }}>
              
                  <>{`${selectedPropertyUnderwrite.type} Underwrite `}<code>{selectedPropertyUnderwrite.id}</code>{` - ${format(parseISO(selectedPropertyUnderwrite.createdOn), 'MM/dd/yyyy')}`}</>

                <Badge badgeContent={propertyUnderwritesData?.length || null} color='primary' style={{ marginLeft: 16, marginBottom: 2 }}/>
                <ArrowDropDownIcon style={{ verticalAlign: 'middle', marginLeft: 8, marginBottom: 4 }}/>
              </span>
            </Tooltip>
          : <div style={{ height: 30 }}/>
        }
      </DialogTitle>
      <IconButton className={classes.closeButton} onClick={onClose}>
        <ClearIcon fontSize="inherit" />
      </IconButton>
      <DialogContent className={classes.dialogContent} dividers>
        <LoadingOverlayWrapper
          className={classes.loadingOverlay}
          active={selectedOpportunity?.isProcessing || isFetchingPropertyUWData || isFetchingPostAcqUWData || isSyncingPurchaseState || propertyUWLoadingOverlayState.isLoading}
          message={selectedOpportunity?.isProcessing ? 'Processing data...' : (isSyncingPurchaseState ? 'Syncing purchase state...' :  ((isFetchingPropertyUWData || isFetchingPostAcqUWData) ? 'Fetching underwrite data...' : propertyUWLoadingOverlayState.message))}
        >
          {selectedOpportunity && selectedContainer && selectedPropertyUnderwrite
            ? <div className={classes.container}>
                {selectedPropertyUnderwrite.module === 'Acq' ? <PropertyUWContent/> : <PostAcqUWContent/>}
                {(txnOppProformaIsOpen || postAcqProformaIsOpen)
                  ? <div>
                    <Resizable ref={r => { proformaResizable = r; }} onResize={onResize} minWidth='300' maxWidth='400'
                      size={{ width: `${proformaWidth}px`, height: ('100%') }}
                      enable={{ top: false, right: false, bottom: false, left: true, topRight: false, bottomRight: false, bottomLeft: false, topLeft: false }}>
                      {selectedPropertyUnderwrite.module === 'Acq' 
                        ? isPostAcquisitionUnderwrite
                          ? <LegacyPostAcqUWProforma resizableWidth={proformaWidth} viewUnderwriteModalIsOpen={open} handleModalClose={handleClose} />
                          : <PropertyUWProforma resizableWidth={proformaWidth} viewUnderwriteModalIsOpen={open} handleModalClose={handleClose} />
                        : <PostAcqUWProforma resizableWidth={proformaWidth} viewUnderwriteModalIsOpen={open} handleModalClose={handleClose} />
                      }
                    </Resizable>
                  </div>
                  : null
                }
              </div>
            : <div className={classes.noUnderwriteContainer}>
                <Typography variant='subtitle2'>No underwrite found</Typography>
              </div>
          }
        </LoadingOverlayWrapper>
      </DialogContent>
      <UnderwriteHistoryPopover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={togglePopover}
        handleChangeUnderwrite={handleChangeUnderwrite}
        asgPropID={asgPropID}
      />
    </DialogWithFocusProvider>
  )
}

const styles = theme => ({
  dialog: {
    minWidth: '200px',
    minHeight: '200px'
  },
  dialogPaper: {
    height: '100%'
  },
  dialogContent: {
    padding: 0
  },
  closeButton: {
    position: 'absolute',
    right: 0,
    top: 0,
    backgroundColor: '#ffffff'
  },
  container: {
    display: 'flex',
    gridTemplateAreas: `'content proforma'`,
    gridTemplateColumns: '1fr auto',
    height: '100%',
    width: '100%'
  },
  loadingOverlay: {
    display: 'flex',
    width: '100%',
    height: '100%'
  },
  noUnderwriteContainer: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center'
  }
});

const mapState = ({ propertyUWState, postAcqUWState, channelWorkflowState, exploreData }) => {
  const isAcq = exploreData.selectedPropertyUnderwrite?.module === "Acq";

  return {
    isPostAcquisitionUnderwrite: propertyUWState.selectedProformaInputsContainer?.isPostAcquisitionUnderwrite,
    selectedOpportunity: isAcq ? propertyUWState.propertyUWClientTxnOpp : postAcqUWState.postAcqUWClientTask,
    selectedContainer: isAcq ? propertyUWState.selectedContainer : postAcqUWState.selectedContainer,
    txnOppProformaIsOpen: propertyUWState.txnOppProformaIsOpen,
    postAcqProformaIsOpen: postAcqUWState.postAcqProformaIsOpen,
    proformaWidth: isAcq ? propertyUWState.proformaWidth : postAcqUWState.proformaWidth,
    isFetchingPropertyUWData: propertyUWState.isFetchingPropertyUWData,
    isFetchingPostAcqUWData: postAcqUWState.isFetchingPostAcqUWData,
    isSyncingPurchaseState: propertyUWState.isSyncingPurchaseState,
    propertyUWLoadingOverlayState: propertyUWState.propertyUWLoadingOverlayState,
    channelWorkflowGridDataUpdate: channelWorkflowState.channelWorkflowGridDataUpdate,
    valuationIsDirty: isAcq ? propertyUWState.valuationIsDirty : postAcqUWState.valuationIsDirty,
    propertyUnderwritesData: exploreData.propertyUnderwritesData,
    selectedPropertyUnderwrite: exploreData.selectedPropertyUnderwrite
  }
};
const mapDispatch = {
  setProformaWidth,
  fetchUWData,
  appNotify,
  setPropertyUWClientTxnOpp,
  setExploreViewUnderwriteModalIsOpen,
  fetchExplorePropertyUnderwrites,
  setSelectedPropertyUnderwrite
};

export default compose(
  withStyles(styles),
  connect(mapState, mapDispatch)
)(ViewUnderwriteModal);